import { useMemo, useState } from 'react'
import { CheckCircle2, Globe, LockKeyhole, Share2 } from 'lucide-react'
import { useApp } from '@/store/AppContext'
import type { AutomationProfile } from '@/types'
import {
  buildAutomationRunPrompt,
  createAutomationProfileId,
  normalizeAutomationProfile,
  saveConversationAutomationProfile,
  saveSharedAutomationProfile,
} from '@/services/automationProfileService'
import './automation-profile-card.css'

interface AutomationProfileData {
  system_name: string
  domain: string
  login_url?: string
  preferred_execution: AutomationProfile['preferredExecution']
  auth_strategy?: AutomationProfile['authStrategy']
  status?: AutomationProfile['status']
  summary: string
  mapped_paths?: string[]
  known_actions?: string[]
  match_keywords?: string[]
  execution_plans?: Array<{ id?: string; name: string; goal: string; steps: string[] }>
  entities?: Array<{ id?: string; name: string; kind?: 'clinic' | 'location' | 'account' | 'resource' | 'other'; loginGroup?: string }>
  notes?: string
}

export function AutomationProfileCard({ data }: { data: AutomationProfileData }) {
  const { activeConversation, settings, updateSettings } = useApp()
  const [savedMode, setSavedMode] = useState<'conversation' | 'shared' | null>(null)

  const normalizedData = useMemo(() => {
    const raw = data as unknown as Record<string, unknown>
    return {
      system_name: String(raw.system_name ?? raw.systemName ?? ''),
      domain: String(raw.domain ?? ''),
      login_url: (raw.login_url ?? raw.loginUrl) as string | undefined,
      preferred_execution: (raw.preferred_execution ?? raw.preferredExecution) as AutomationProfile['preferredExecution'],
      auth_strategy: (raw.auth_strategy ?? raw.authStrategy) as AutomationProfile['authStrategy'] | undefined,
      status: (raw.status ?? undefined) as AutomationProfile['status'] | undefined,
      summary: String(raw.summary ?? ''),
      mapped_paths: (raw.mapped_paths ?? raw.mappedPaths ?? []) as string[],
      known_actions: (raw.known_actions ?? raw.knownActions ?? []) as string[] | undefined,
      match_keywords: (raw.match_keywords ?? raw.matchKeywords ?? []) as string[] | undefined,
      execution_plans: (raw.execution_plans ?? raw.executionPlans ?? []) as AutomationProfileData['execution_plans'],
      entities: (raw.entities ?? []) as AutomationProfileData['entities'],
      notes: (raw.notes ?? undefined) as string | undefined,
    }
  }, [data])

  const profile = useMemo<AutomationProfile>(() => {
    const now = new Date().toISOString()
    return normalizeAutomationProfile({
      id: createAutomationProfileId(normalizedData.system_name, normalizedData.domain),
      systemName: normalizedData.system_name,
      domain: normalizedData.domain,
      loginUrl: normalizedData.login_url,
      preferredExecution: normalizedData.preferred_execution,
      authStrategy: normalizedData.auth_strategy,
      status: normalizedData.status,
      summary: normalizedData.summary,
      mappedPaths: normalizedData.mapped_paths ?? [],
      knownActions: normalizedData.known_actions,
      matchKeywords: normalizedData.match_keywords,
      executionPlans: normalizedData.execution_plans?.map((plan, index) => ({
        id: plan.id ?? `${createAutomationProfileId(normalizedData.system_name, normalizedData.domain)}-plan-${index + 1}`,
        name: plan.name,
        goal: plan.goal,
        steps: plan.steps,
      })),
      entities: normalizedData.entities?.map((entity, index) => ({
        id: entity.id ?? `${createAutomationProfileId(normalizedData.system_name, normalizedData.domain)}-entity-${index + 1}`,
        name: entity.name,
        kind: entity.kind ?? 'other',
        loginGroup: entity.loginGroup,
      })),
      notes: normalizedData.notes,
      conversationId: activeConversation?.id ?? undefined,
      createdAt: now,
      updatedAt: now,
    })
  }, [activeConversation?.id, normalizedData])

  const saveToConversation = () => {
    saveConversationAutomationProfile(activeConversation?.id, profile)
    window.dispatchEvent(new CustomEvent('sine:automation-profile-updated'))
    if (activeConversation?.appMode === 'studio') {
      window.dispatchEvent(new CustomEvent('sine:run-automation-profile', {
        detail: {
          profile,
          prompt: `${buildAutomationRunPrompt(profile)} Fortsett Studio-kartleggingen nå, be om innlogging hvis nødvendig, test banen, og fullfør ikke før automasjonen faktisk er klar til senere bruk.`,
        },
      }))
    }
    setSavedMode('conversation')
  }

  const saveAndShare = () => {
    saveConversationAutomationProfile(activeConversation?.id, profile)
    updateSettings({
      sharedAutomationProfiles: saveSharedAutomationProfile(settings, profile),
    })
    window.dispatchEvent(new CustomEvent('sine:automation-profile-updated'))
    if (activeConversation?.appMode === 'studio') {
      window.dispatchEvent(new CustomEvent('sine:run-automation-profile', {
        detail: {
          profile,
          prompt: `${buildAutomationRunPrompt(profile)} Fortsett Studio-kartleggingen nå, be om innlogging hvis nødvendig, test banen, og fullfør ikke før automasjonen faktisk er klar til senere bruk.`,
        },
      }))
    }
    setSavedMode('shared')
  }

  return (
    <div className="automation-card">
      <div className="automation-card-header">
        <div>
          <span className="automation-card-badge">
            <LockKeyhole size={12} />
            Kartlagt automasjon
          </span>
          <div className="automation-card-title">{normalizedData.system_name}</div>
        </div>
        <Globe size={16} style={{ color: '#7d7d7d', flexShrink: 0 }} />
      </div>

      <p className="automation-card-desc">{normalizedData.summary}</p>

      <div className="automation-card-grid">
        <div className="automation-card-field">
          <span className="automation-card-label">Domene</span>
          <span className="automation-card-value">{normalizedData.domain}</span>
        </div>
        <div className="automation-card-field">
          <span className="automation-card-label">Foretrukket kjøring</span>
          <span className="automation-card-value">{normalizedData.preferred_execution}</span>
        </div>
        {profile.status && (
          <div className="automation-card-field">
            <span className="automation-card-label">Status</span>
            <span className="automation-card-value">{profile.status}</span>
          </div>
        )}
        {profile.authStrategy && (
          <div className="automation-card-field">
            <span className="automation-card-label">Auth</span>
            <span className="automation-card-value">{profile.authStrategy}</span>
          </div>
        )}
        {normalizedData.login_url && (
          <div className="automation-card-field" style={{ gridColumn: '1 / -1' }}>
            <span className="automation-card-label">Login-URL</span>
            <span className="automation-card-value">{normalizedData.login_url}</span>
          </div>
        )}
      </div>

      {profile.knownActions && profile.knownActions.length > 0 && (
        <div>
          <span className="automation-card-label">Kjente handlinger</span>
          <div className="automation-card-paths">
            {profile.knownActions.map(action => (
              <span key={action} className="automation-card-path">{action}</span>
            ))}
          </div>
        </div>
      )}

      {(normalizedData.mapped_paths ?? []).length > 0 && (
        <div>
          <span className="automation-card-label">Kartlagt bane</span>
          <div className="automation-card-paths">
            {(normalizedData.mapped_paths ?? []).map(path => (
              <span key={path} className="automation-card-path">{path}</span>
            ))}
          </div>
        </div>
      )}

      {profile.entities && profile.entities.length > 0 && (
        <div>
          <span className="automation-card-label">Lagrede enheter</span>
          <div className="automation-card-paths">
            {profile.entities.map(entity => (
              <span key={entity.id} className="automation-card-path">{entity.name}</span>
            ))}
          </div>
        </div>
      )}

      {profile.executionPlans && profile.executionPlans.length > 0 && (
        <div className="automation-card-field">
          <span className="automation-card-label">Standard plan</span>
          <span className="automation-card-value">
            {profile.executionPlans[0].steps.join(' -> ')}
          </span>
        </div>
      )}

      {normalizedData.notes && (
        <div className="automation-card-field">
          <span className="automation-card-label">Notater</span>
          <span className="automation-card-value">{normalizedData.notes}</span>
        </div>
      )}

      {savedMode ? (
        <div className="automation-card-confirmed">
          <CheckCircle2 size={14} style={{ marginRight: 6, verticalAlign: 'text-bottom' }} />
          {savedMode === 'shared'
            ? 'Lagt til i denne chatten og delt i Datakontroll.'
            : 'Lagt til i denne Studio-chatten.'}
        </div>
      ) : (
        <div className="automation-card-actions">
          <button className="automation-card-btn automation-card-btn-primary" onClick={saveToConversation}>
            Lagre i denne chatten
          </button>
          <button className="automation-card-btn automation-card-btn-secondary" onClick={saveAndShare}>
            <Share2 size={13} style={{ marginRight: 6, verticalAlign: 'text-bottom' }} />
            Lagre og del i Datakontroll
          </button>
        </div>
      )}
    </div>
  )
}
